import type { AppStatus, Sample } from '../app/types.ts';
import { Sparkline } from './Sparkline.tsx';

interface AppCardProps {
  app: AppStatus;
  samples: Sample[];
}

/** 60 Hz tick budget in ms — gauges warn as max approaches it. */
const TICK_BUDGET_MS = 16.6;

function gaugeClass(value: number | undefined, warnAt: number): string {
  if (value === undefined) return '';
  if (value >= TICK_BUDGET_MS) return 'bad';
  if (value >= warnAt) return 'warn';
  return '';
}

function fmt(value: number | undefined, digits = 1): string {
  return value === undefined ? '—' : value.toFixed(digits);
}

/**
 * Games per hour over the session history, summed from per-sample deltas.
 * Negative deltas (counter reset on server restart) are skipped, so a
 * redeploy mid-session doesn't poison the rate.
 */
export function gamesPerHour(
  samples: Sample[],
  field: 'gamesStarted' | 'gamesFinished',
): number | null {
  if (samples.length < 2) return null;
  let deltas = 0;
  for (let i = 1; i < samples.length; i++) {
    const d = samples[i][field] - samples[i - 1][field];
    if (d > 0) deltas += d;
  }
  const hours = (samples[samples.length - 1].t - samples[0].t) / 3_600_000;
  return hours > 0 ? deltas / hours : null;
}

export function AppCard({ app, samples }: AppCardProps) {
  const running = app.machines.filter((m) => m.state === 'started');
  const parked = app.machines.filter((m) => m.state === 'stopped' || m.state === 'suspended');
  // Fly states between "requested" and "started" (created, starting, …).
  const booting = app.machines.filter(
    (m) => !['started', 'stopped', 'suspended', 'destroyed'].includes(m.state),
  );
  const cluster = app.cluster;
  const pending = cluster?.pending ?? [];
  // Actively scaling = clone-in-flight records (written BEFORE the Machines
  // API create) + Machines caught mid-boot by the Fly API.
  const scalingCount = pending.length + booting.length;
  const activeByMachine = new Map((cluster?.active ?? []).map((a) => [a.machineId, a]));
  const pendingByMachine = new Set(pending.map((p) => p.machineId));
  // Pending clones the Machines API doesn't list yet (create still in flight).
  const pendingUnlisted = pending.filter(
    (p) => !p.machineId || !app.machines.some((m) => m.id === p.machineId),
  );
  // Fleet-wide rooms/capacity from the registry — the single-process probes
  // (/admin/status, /metrics) sample one random Machine when N are running.
  const fleetRooms = cluster ? cluster.active.reduce((sum, a) => sum + a.roomCount, 0) : null;
  const fleetCapacity = cluster ? cluster.active.reduce((sum, a) => sum + a.capacity, 0) : null;
  const cold = app.flyError === null && running.length === 0;
  const unreachable =
    !cold && app.flyError === null && !app.health && !app.status && !app.cluster && !app.metrics;
  const draining = !!(
    app.health?.draining ||
    app.status?.draining ||
    app.metrics?.draining ||
    cluster?.drain
  );
  const image = running[0]?.image ?? app.machines[0]?.image ?? null;
  const m = app.metrics;

  const classes = ['card'];
  if (app.live) classes.push('live');
  if (draining) classes.push('draining');

  return (
    <section class={classes.join(' ')}>
      <header class="card-head">
        <h2>{app.name}</h2>
        {app.live && <span class="badge badge-live">live</span>}
        {draining && <span class="badge badge-drain">draining</span>}
        {cold && <span class="badge badge-cold">cold / scaled to zero</span>}
        {unreachable && <span class="badge badge-bad">unreachable</span>}
        {app.machines.length > 0 && (
          <span class="badge">
            {running.length} running{parked.length > 0 ? ` · ${parked.length} stopped` : ''}
          </span>
        )}
        {scalingCount > 0 && <span class="badge badge-scale">scaling +{scalingCount}</span>}
      </header>

      {app.flyError !== null && <div class="card-error">Fly API: {app.flyError}</div>}

      {(app.machines.length > 0 || pendingUnlisted.length > 0) && (
        <ul class="machines">
          {app.machines.map((machine) => {
            const active = activeByMachine.get(machine.id);
            return (
              <li key={machine.id}>
                <span class={`status-dot ${machine.state === 'started' ? 'online' : ''}`} />
                <span class="machine-state">{machine.state}</span>
                <span class="machine-region">{machine.region}</span>
                <span class="machine-id">{machine.id.slice(0, 8)}</span>
                {active && (
                  <span class="machine-rooms">
                    {active.roomCount}/{active.capacity} rooms
                  </span>
                )}
                {active?.clone && <span class="machine-tag">clone</span>}
                {active?.draining && <span class="machine-tag">cordoned</span>}
                {active && machine.state !== 'started' && (
                  <span class="machine-tag stale">ghost</span>
                )}
                {pendingByMachine.has(machine.id) && <span class="machine-tag">scaling</span>}
              </li>
            );
          })}
          {pendingUnlisted.map((p) => (
            <li key={p.name}>
              <span class="status-dot" />
              <span class="machine-state">requested</span>
              <span class="machine-region">{p.region}</span>
              <span class="machine-id">{p.name}</span>
              <span class="machine-tag">scaling</span>
            </li>
          ))}
        </ul>
      )}

      {image && (
        <div class="deploy" title={image}>
          deploy: {image.split('/').pop()}
        </div>
      )}

      {!cold && (
        <>
          <dl class="kv">
            <div>
              <dt>rooms{fleetRooms !== null ? ' (fleet)' : ''}</dt>
              <dd>
                {fleetRooms ?? app.status?.roomCount ?? m?.rooms ?? '—'} /{' '}
                {fleetCapacity ?? m?.roomsMax ?? '—'}
                {(fleetRooms !== null
                  ? fleetCapacity !== null && fleetRooms >= fleetCapacity
                  : app.status?.full) && <span class="badge badge-bad">full</span>}
              </dd>
            </div>
            <div>
              <dt>connections</dt>
              <dd>{m?.connections ?? '—'}</dd>
            </div>
            <div>
              <dt>protocol</dt>
              <dd>{app.health?.protocolVersion ?? '—'}</dd>
            </div>
            <div>
              <dt>games finished</dt>
              <dd>{m?.gamesFinishedTotal ?? '—'}</dd>
            </div>
            <div>
              <dt>games/hr (start/fin)</dt>
              <dd>
                {fmt(gamesPerHour(samples, 'gamesStarted') ?? undefined)} /{' '}
                {fmt(gamesPerHour(samples, 'gamesFinished') ?? undefined)}
              </dd>
            </div>
            <div class={gaugeClass(m?.simTickMsMax, 12)}>
              <dt>sim tick ms (avg/max)</dt>
              <dd>
                {fmt(m?.simTickMsAvg)} / {fmt(m?.simTickMsMax)}
              </dd>
            </div>
            <div class={gaugeClass(m?.simGapMsMax, 25)}>
              <dt>sim gap ms (avg/max)</dt>
              <dd>
                {fmt(m?.simGapMsAvg)} / {fmt(m?.simGapMsMax)}
              </dd>
            </div>
          </dl>

          <div class="sparks">
            <Sparkline samples={samples} field="connections" color="var(--neutron)" label="conn" />
            <Sparkline samples={samples} field="rooms" color="var(--solar)" label="rooms" />
            <Sparkline
              samples={samples}
              field="tickMsMax"
              color="var(--blackhole)"
              label="tick max ms"
            />
            <Sparkline
              samples={samples}
              field="gapMsMax"
              color="var(--danger)"
              label="gap max ms"
            />
          </div>
        </>
      )}
    </section>
  );
}
